import { useDispatch } from 'react-redux';
import {
    Col,
    Alert,
    Spinner,
    CardBody,
    Card,
    CardHeader,
    Row,
} from 'reactstrap';
import { useNavigate, Link } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ODataGrid } from '../../Components/OElements/ODataGrid.js';
import { getPersonAssignedItemsSummary, getPersonAssignedItemsSummaryAll, getPersonAssignedItemsSummaryQuery, getPersonAssignmentsQuery } from '../../slices/thunks.js';
import { useUser } from '../../Components/Hooks/useUserHooks.js';
import usePersonAssignmentsList from './List/usePersonAssignmentsList.js';
import PPEAssignmentDetailRow from './DetailPanels/PPEAssignmentDetailRow.js';
import { getComplianceStatusColor, getPercentageCoverageItemsPositionColor, getTotalAssignmentBadgeColor, getTotalCostBadgeColor } from './constants.js';
import { Badge } from '@mantine/core';

const PersonalProtectiveEquipmentPersonAssignments = () => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const { user } = useUser();


    const {
        refetchRemoteData,
        refetchAssignedItems,
        handleClearCacheAndRefetch,
        handleDataChange
    } = usePersonAssignmentsList();

    const selectorCategories = createSelector(
        state => state.PersonalProtectiveEquipments,
        data => ({
            dataList: data.personalProtectiveEquipmentList,
            isLoadingList: data.isLoadingList,
            isPersonalProtectiveEquipmentSuccess: data.isPersonalProtectiveEquipmentSuccess,
        })
    );

    const { dataList, isLoadingList } = useSelector(selectorCategories);

    useEffect(() => {
        dispatch(getPersonAssignedItemsSummaryQuery());
    }, []);


    const columns = useMemo(
        () => [
            { header: 'Rut', accessorKey: 'person.rut', size: 'auto', },
            { header: 'Persona', accessorKey: 'person.name', size: 'auto', },
            { header: 'Centro De Costo', accessorKey: 'person.cost_center.code', size: 'auto', dataType: 'multi-select', },
            { header: 'Departamento', accessorKey: 'person.management.name', size: 'auto', dataType: 'multi-select', },
            { header: 'Establecimiento', accessorKey: 'establishment.name', size: 'auto', dataType: 'multi-select', },
            {
                header: 'Porcentaje de Cumplimiento',
                accessorKey: 'coverage_pct_global',
                Cell: cellProps => {
                    const cellValue = cellProps.row.original.coverage_pct_global;

                    return (
                        <Badge
                            color={getPercentageCoverageItemsPositionColor(cellValue)}
                            variant='light'
                            tt='capitalize'
                        >
                            <i>%</i> {cellValue}
                        </Badge>
                    );
                }
            },
            {
                header: 'Estado de Cumplimiento',
                accessorKey: 'coverage_pct_global',
                Cell: cellProps => {
                    const cellValue = cellProps.row.original.compliance_status;

                    return (
                        <Badge
                            color={getComplianceStatusColor(cellValue)}
                            variant='light'
                            tt='capitalize'
                        >
                            {cellValue}
                        </Badge>
                    );
                }
            },
            {
                header: 'Total EPP Asignados',
                accessorKey: 'total_assigned_items',
                Cell: cellProps => {
                    const cellValue = cellProps.row.original.total_assigned_items;

                    return (
                        <Badge
                            color={getTotalAssignmentBadgeColor(cellValue)}
                            variant='light'
                            tt='capitalize'
                        >
                            <i className='bx bx-package me-1'></i>
                            {cellValue}
                        </Badge>
                    );
                }
            },
            {
                header: 'Costo Total',
                accessorKey: 'total_assigned_cost',
                align: 'right',
                Cell: cellProps => {
                    const cellValue = cellProps.row.original.total_assigned_cost;

                    return (
                        <Badge
                            color={getTotalCostBadgeColor(cellValue)}
                            variant='light'
                            tt='capitalize'
                        >
                            $ {cellValue}
                        </Badge>
                    );
                },
            },
        ],
    );

    return (
        <React.Fragment>
            <Row>
                <Col lg={12}>
                    <Card>

                        <CardBody>
                            <ODataGrid
                                columns={columns}
								// data={powerGenerationHourMeterList || []}
								getDataFunc={getPersonAssignmentsQuery}
								// isLoadingData={isLoadingList}
								nameFile={'Personas EPP Asignados'}
								queryPopulate={[]}
								remoteOperations={{
									pagination: true,
									sorting: true,
									filtering: true,
								}}
                                getRowIdTable={row => row._id}
                                tableTitle='Lista de Items Asignados'
                                masterDetailComponent={useCallback(({ row }) => {
                                    if (!row.getIsExpanded()) return null;
                                    return (
                                        <PPEAssignmentDetailRow
                                            row={row}
                                            externalRefetch={refetchAssignedItems}
                                            onStatusChange={handleClearCacheAndRefetch}
                                        />
                                    );
                                }, [
                                    refetchAssignedItems, 
                                    handleClearCacheAndRefetch
                                ])}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default PersonalProtectiveEquipmentPersonAssignments;
